/*!
 * clyro-shop.js - the ONE fixed purchase module for template-simple.
 *
 * This file is FIXED infrastructure. The chat agent never rewrites it; sections
 * only wire markup to the hooks below. Vanilla fetch + Alpine, no other deps.
 *
 * Alpine store `cart`:
 *   { items:[], item_count:0, total_price:0, open:false, loading:false, loadingKey:null }
 *
 * window.clyroShop public surface (sections + layout depend on these EXACT names):
 *   add(variantId, qty=1, sellingPlanId=null)
 *                                -> POST /cart/add.js    -> silent refresh()
 *   change(lineKey, qty)         -> POST /cart/change.js -> refresh()
 *   refresh()                    -> GET  /cart.js        -> updates Alpine.store('cart')
 *   openCart()/closeCart()       -> native drawer compatibility helpers
 *   money(cents)                 -> Shopify-style price string
 *   variantFrom(optionValues, product) -> { id, price, available,
 *       compare_at_price, featured_image } | null
 *   buyBox(seed)                 -> Alpine x-data factory for the product
 *       buy-box (variant pick, live price, gallery sync, bundle-tier qty).
 *       seed = { qty, tier, sel: [option values], price: fallback cents }.
 *       The section supplies markup + <script type="application/json"
 *       x-ref="pdata">{"variants": [...]}</script>; behavior lives HERE.
 */
(function () {
  if (window.clyroPurchaseInitialized) return;
  window.clyroPurchaseInitialized = true;
  var operationSequence = 0;
  var operationPrefix = Date.now().toString(36) + ':';
  var BASE_CART = { items: [], item_count: 0, total_price: 0, open: false, loading: false, loadingKey: null };
  var addRequests = Object.create(null);
  var taskTail = Promise.resolve();
  var closeGeneration = 0;
  var cartRevision = 0;
  var checkoutLocked = false;
  document.addEventListener('clyro:drawer-closed', function () { closeGeneration += 1; pendingDrawerIntent = null; drawerStatus(''); });

  function runCartTask(task) {
    var next = taskTail.then(task);
    taskTail = next.catch(function () {});
    return next;
  }
  function whenIdle() {
    var observed = taskTail;
    return observed.then(function () { return observed === taskTail ? null : whenIdle(); });
  }
  function requestJSON(url, options) {
    options = options || {};
    if (String(options.method || 'GET').toUpperCase() !== 'GET') {
      options.headers = Object.assign({ 'X-Requested-With': 'XMLHttpRequest' }, options.headers || {});
    }
    return fetch(url, options).then(function (response) {
      return response.json().catch(function () {
        throw new Error('Shopify could not be reached. Check your cart before trying again.');
      }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'The cart could not be updated.');
          error.status = response.status;
          throw error;
        }
        return data;
      });
    });
  }

  function freshProduct(productUrl) {
    var url = new URL(productUrl, window.location.href);
    if (url.origin !== window.location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) {
      return Promise.reject(new Error('Please open this product to choose an option.'));
    }
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    url.search = '';
    url.hash = '';
    url.searchParams.set('_clyro_fresh', Date.now());
    return requestJSON(url.toString(), {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    }).then(function (product) {
      if (!product || !Array.isArray(product.variants)) throw new Error('Product availability could not be checked. Please open the product and try again.');
      return product;
    });
  }

  function quickAddVariant(productUrl, preferredId) {
    return freshProduct(productUrl).then(function (product) {
      // Homepage quick-add is explicitly configured as first available.
      var variant = product.variants.find(function (v) { return String(v.id) === String(preferredId) && v.available; });
      if (!variant) variant = product.variants.find(function (v) { return v.available; });
      if (!variant) throw new Error('This product is currently sold out. Please choose another design.');
      if (product.requires_selling_plan || variant.requires_selling_plan) throw new Error('Please open this product to choose a purchase plan.');
      return variant;
    });
  }

  function showPdpAddError(id, message) {
    if (!/\/products\/[^/]+\/?$/.test(window.location.pathname)) return;
    var nodes = document.querySelectorAll('script[x-ref="pdata"]');
    Array.from(nodes).forEach(function (node) {
      try {
        if (!JSON.parse(node.textContent).variants.some(function (v) { return String(v.id) === String(id); })) return;
        var scope = node.closest('[data-clyro-buybox], .pdp');
        var form = scope && scope.querySelector('form[action*="/cart/add"]');
        if (!form) return;
        var status = form.querySelector('[data-clyro-pdp-error]');
        if (!status && message) {
          status = document.createElement('p'); status.setAttribute('data-clyro-pdp-error', '');
          status.setAttribute('role', 'alert'); form.appendChild(status);
        }
        if (status) { status.textContent = message || ''; status.hidden = !message; }
      } catch (_) {}
    });
  }

  function verifyPdpVariant(id, planId) {
    // Only verify against this PDP when its embedded product actually owns the
    // requested ID. Drawer upsells and unrelated featured products stay separate.
    if (!/\/products\/[^/]+\/?$/.test(window.location.pathname)) return Promise.resolve();
    var nodes = document.querySelectorAll('script[x-ref="pdata"]');
    var ownsVariant = Array.from(nodes).some(function (node) {
      try { return JSON.parse(node.textContent).variants.some(function (v) { return String(v.id) === String(id); }); }
      catch (_) { return false; }
    });
    if (!ownsVariant) return Promise.resolve();
    return freshProduct(window.location.pathname).then(function (product) {
      var variant = product.variants.find(function (v) { return String(v.id) === String(id); });
      // Never substitute a different option for a shopper's explicit PDP choice.
      if (!variant) throw new Error('This option has changed. Refresh the product page and choose it again.');
      if (!variant.available) throw new Error('The selected option is now sold out. Please choose another option.');
      if ((product.requires_selling_plan || variant.requires_selling_plan) && !planId) throw new Error('Choose a purchase plan for this option.');
    });
  }
  function cartError(error) {
    document.dispatchEvent(new CustomEvent('clyro:cart-error', { detail: { message: error.message } }));
  }
  function readCart(options) {
    options = options || {};
    return requestJSON(cartUrl('cart.js'), {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    }).then(function (data) {
      if (!data || !Array.isArray(data.items)) throw new Error('The cart could not be loaded.');
      attachQuantityRules(data);
      applyCart(data, options);
      return data;
    });
  }

  function attachQuantityRules(data) {
    if (!data || !Array.isArray(data.items)) return data;
    data.items.forEach(function (item) {
      // Shopify's cart response is the customer-session authority. Quantity
      // rules are catalog constraints, not a client-side inventory estimate.
      // Inventory validation itself remains with cart/add.js and cart/change.js.
      var source = item.quantity_rule || {};
      var max = source.max == null ? Infinity : Number(source.max);
      item._clyroLimit = {
        max: Number.isFinite(max) ? max : Infinity,
        min: Math.max(1, Number(source.min) || 1),
        increment: Math.max(1, Number(source.increment) || 1)
      };
    });
    return data;
  }

  function validateQuantity(item, quantity) {
    var rule = item && item._clyroLimit;
    // Older or customized cart payloads may omit quantity_rule. Never invent a
    // zero-stock result; let Shopify validate the requested change instead.
    if (!rule) return;
    if (quantity > rule.max) throw new Error('The maximum quantity available is already in your cart. Please reduce the quantity if needed.');
    if (quantity && (quantity < rule.min || quantity % rule.increment !== 0)) throw new Error('This quantity does not meet the product quantity rules.');
  }

  function cartUrl(path) {
    var root = '/';
    try {
      root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (_) {}
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(path || '').replace(/^\/+/, '');
  }

  function store() {
    return (window.Alpine && window.Alpine.store) ? window.Alpine.store('cart') : null;
  }

  function applyCart(data, options) {
    options = options || {};
    var c = store();
    if (!data) return;
    data._clyroRevision = ++cartRevision;
    if (c) {
      c.items = Array.isArray(data.items) ? data.items : [];
      c.item_count = typeof data.item_count === 'number' ? data.item_count : 0;
      c.total_price = typeof data.total_price === 'number' ? data.total_price : 0;
    }
    // Socket contract (shared with the full base): transplanted headers carry
    // static [data-clyro-cart-count] badges — keep them live — and external
    // listeners (transfer smoke, integrations) hear 'cart:updated'.
    try {
      var badges = document.querySelectorAll('[data-clyro-cart-count]');
      var itemCount = typeof data.item_count === 'number' ? data.item_count : 0;
      for (var i = 0; i < badges.length; i++) badges[i].textContent = String(itemCount);
      // State synchronization never means "open the drawer". The native drawer
      // listens to this complete state event only to render current contents.
      document.dispatchEvent(new CustomEvent('clyro:cart-state', { detail: data }));
      // Preserve the existing public update contract for user-driven changes.
      if (options.notify === true) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      }
    } catch (e) { /* never let socket sync break the cart */ }
  }

  function updateCartCount(count) {
    var next = Math.max(0, Number(count) || 0);
    var c = store();
    if (c) c.item_count = next;
    try {
      var badges = document.querySelectorAll('[data-clyro-cart-count]');
      for (var i = 0; i < badges.length; i++) badges[i].textContent = String(next);
    } catch (_) {}
  }

  function setLoading(on, key) {
    document.dispatchEvent(new CustomEvent('clyro:cart-loading', { detail: { busy: !!on } }));
    var c = store();
    if (!c) return;
    if (on) {
      c.loading = true;
      if (key != null) c.loadingKey = String(key);
    } else {
      c.loading = false;
      c.loadingKey = null;
    }
  }

  // ── money ────────────────────────────────────────────────────────
  // Shopify ships prices in cents. Use the live shop currency symbol if the
  // storefront exposed it, otherwise fall back to a plain "$" prefix.
  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var amount = (n / 100).toFixed(2);
    var symbol = '$';
    try {
      var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (cur === 'EUR') symbol = '€';
      else if (cur === 'GBP') symbol = '£';
      else if (cur === 'JPY') { symbol = '¥'; amount = String(Math.round(n / 100)); }
    } catch (_) {}
    return symbol + amount;
  }

  // ── variant resolution ───────────────────────────────────────────
  // optionValues = array of selected option strings, in option order.
  // product = a Shopify product JSON object (product.variants[]).
  function variantFrom(optionValues, product) {
    if (!product || !Array.isArray(product.variants)) return null;
    var opts = optionValues || [];
    var match = product.variants.find(function (v) {
      return (opts[0] == null || v.option1 === opts[0])
          && (opts[1] == null || v.option2 === opts[1])
          && (opts[2] == null || v.option3 === opts[2]);
    });
    if (!match) return null;
    return {
      id: match.id,
      price: match.price,
      available: !!match.available,
      compare_at_price: match.compare_at_price || 0,
      requires_selling_plan: !!match.requires_selling_plan,
      featured_image: match.featured_image || null
    };
  }

  // ── cart UI compatibility helpers ────────────────────────────────
  // Queue only explicit cart intent, never state updates or page hydration.
  var pendingDrawerIntent = null;
  var drawerRetryTimer = null;
  var drawerIntentStatus = null;

  function drawerStatus(message) {
    if (!message && !drawerIntentStatus) return;
    if (!document.createElement || !document.body) return;
    if (!drawerIntentStatus) {
      drawerIntentStatus = document.createElement('div');
      drawerIntentStatus.setAttribute('role', 'status');
      drawerIntentStatus.setAttribute('aria-live', 'polite');
      drawerIntentStatus.style.cssText = 'position:fixed;bottom:20px;left:20px;right:20px;z-index:2147483647;padding:12px;background:#10142e;color:white;text-align:center;font:16px sans-serif';
      document.body.appendChild(drawerIntentStatus);
    }
    drawerIntentStatus.textContent = message;
    drawerIntentStatus.hidden = !message;
  }

  function flushDrawerIntent() {
    if (!pendingDrawerIntent) return;
    var intent = pendingDrawerIntent;
    if (Date.now() - intent.at > 10000) {
      pendingDrawerIntent = null;
      drawerStatus('Your cart could not open. Please refresh this page and try again.');
      return;
    }
    // Clear before boot: initialization emits ready synchronously.
    pendingDrawerIntent = null;
    try {
      if (window.clyroNativeCartBoot) window.clyroNativeCartBoot(document);
      // A delayed add notification must not render an old snapshot after
      // intervening quantity, removal, or discount changes.
      if (intent.attempted && intent.type === 'clyro:cart-added') intent.detail.cart = null;
      intent.attempted = true;
      document.dispatchEvent(new CustomEvent(intent.type, { detail: intent.detail }));
    } catch (_) {}
    if (intent.detail.accepted) drawerStatus('');
    else {
      pendingDrawerIntent = intent;
      drawerStatus('Opening your cart…');
      if (!drawerRetryTimer) drawerRetryTimer = window.setTimeout(function () {
        drawerRetryTimer = null;
        flushDrawerIntent();
      }, 200);
    }
  }

  function requestDrawer(type, detail) {
    pendingDrawerIntent = { type: type, detail: detail, at: Date.now() };
    flushDrawerIntent();
  }
  document.addEventListener('clyro:cart-drawer:ready', flushDrawerIntent);

  function onCartTriggerClick(event) {
    var target = event.target;
    var trigger = target && target.closest ? target.closest('[data-cart-drawer-open], #clyro-cart-toggle') : null;
    if (!trigger) return;
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.isTrusted === false) return;
    requestDrawer('clyro:cart-drawer:open', { source: 'header-cart', userEvent: event, accepted: false });
  }

  function openCart() {
    try {
      document.dispatchEvent(new CustomEvent('clyro:cart-drawer:open', {
        detail: { source: 'clyro-shop' }
      }));
    } catch (_) {}
  }
  function closeCart() {
    pendingDrawerIntent = null;
    drawerStatus('');
    try { document.dispatchEvent(new CustomEvent('clyro:cart-drawer:close')); } catch (_) {}
  }

  // ── cart network ops (Shopify AJAX API) ──────────────────────────
  function refresh(options) {
    options = options || {};
    return runCartTask(function () { return readCart(options); }).catch(function (error) {
      cartError(error);
      if (options.throwOnError) throw error;
      return null;
    });
  }

  function add(variantId, qty, sellingPlanId, options) {
    options = options || {};
    if (checkoutLocked) return Promise.reject(new Error('Checkout is being prepared. Please wait.'));
    var quantity = Math.max(1, Math.floor(Number(qty) || 1));
    var id = Number(variantId) || variantId;
    var planId = Number(sellingPlanId) || null;
    var line = { id: id, quantity: quantity };
    if (planId) line.selling_plan = planId;
    if (options.properties) line.properties = options.properties;
    var requestKey = JSON.stringify(line);
    var generation = closeGeneration;
    if (!addRequests[requestKey]) {
      var operationId = operationPrefix + (++operationSequence);
      addRequests[requestKey] = runCartTask(function () {
        if (!id) throw new Error('Please choose a product option.');
        setLoading(true, 'add');
        showPdpAddError(id, '');
        return verifyPdpVariant(id, planId).then(function () { return requestJSON(cartUrl('cart/add.js'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [line] })
        }); }).then(function (item) {
          // Never retry a confirmed POST when its following read fails.
          return readCart({ notify: false }).catch(function () { return null; }).then(function (data) {
            requestDrawer('clyro:cart-added', {
              success: true, operationId: operationId, source: options.source || 'clyro:add', item: item, cart: data, quantity: quantity,
              open: options.open !== false && generation === closeGeneration
            });
            return item;
          });
        }).catch(function (error) {
          // A rejected/ambiguous add can leave a changed cart; reconcile once,
          // but never replay the POST or choose another PDP variant automatically.
          return readCart({ notify: true }).catch(function () { return null; }).then(function () { throw error; });
        }).finally(function () { setLoading(false); });
      }).catch(function (error) { showPdpAddError(id, error.message); cartError(error); throw error; })
        .finally(function () { delete addRequests[requestKey]; });
    }
    return addRequests[requestKey].catch(function (error) {
      if (options.throwOnError) throw error;
      return null;
    });
  }

  function mutate(path, payload) {
    if (checkoutLocked) return Promise.reject(new Error('Checkout is being prepared. Please wait.'));
    return runCartTask(function () {
      setLoading(true, payload.id || 'update');
      var preflight = path === 'cart/change.js' && payload.quantity > 0
        ? readCart({}).then(function (fresh) {
          var item = fresh.items.find(function (line) { return line.key === payload.id; });
          if (!item) throw new Error('This cart item changed. Please try again.');
          validateQuantity(item, payload.quantity);
        }) : Promise.resolve();
      return preflight.then(function () { return requestJSON(cartUrl(path), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }); }).then(function (data) {
        if (!data || !Array.isArray(data.items)) throw new Error('The cart could not be updated.');
        return readCart({ notify: true });
      }).catch(function (error) {
        return readCart({ notify: true }).catch(function () {}).then(function () { throw error; });
      }).finally(function () { setLoading(false); });
    }).catch(function (error) { cartError(error); throw error; });
  }
  function change(lineKey, qty) {
    if (!lineKey) return Promise.reject(new Error('This cart item is no longer available.'));
    return mutate('cart/change.js', { id: lineKey, quantity: Math.max(0, Math.floor(Number(qty) || 0)) });
  }
  function update(payload) { return mutate('cart/update.js', payload); }

  function applyDiscountForCheckout(code) {
    var cleanCode = String(code || '').trim();
    if (!cleanCode) return Promise.resolve(null);
    // This operation is part of the checkout transaction, so it is allowed
    // after ordinary cart writes have been locked out. Applying the code to
    // the cart avoids the redirect chain through /discount/{code}.
    return runCartTask(function () {
      return requestJSON(cartUrl('cart/update.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: cleanCode })
      }).then(function (data) {
        if (!data || !Array.isArray(data.items)) throw new Error('The discount could not be applied. Please try again.');
        attachQuantityRules(data);
        applyCart(data, { notify: true });
        return data;
      });
    });
  }

  function clearIfUnchanged(expected, allowed) {
    return runCartTask(function () {
      return readCart({}).then(function (fresh) {
        var signature = function (data) { return data.items.map(function (item) { return item.key + ':' + item.quantity; }).sort().join('|'); };
        if (checkoutLocked || !allowed() || signature(expected) !== signature(fresh)) return fresh;
        return requestJSON(cartUrl('cart/clear.js'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function () { return readCart({ notify: true }); });
      });
    });
  }

  // ── product buy-box (Alpine x-data factory) ──────────────────────
  // Aura-grade behaviors, fixed here so sections stay markup-only:
  // ?variant= URL sync (read at init, replaceState on pick), a
  // 'variant:change' event for other sections, sold-out option-value
  // detection, percent OR fixed tier discounts, per-unit tier pricing.
  function buyBox(seed) {
    seed = seed || {};
    function tierCents(q, price, amt, dtype) {
      if (dtype === 'fixed') return Math.max(0, Math.round(q * price - amt * 100));
      return Math.round(q * price * (100 - amt) / 100);
    }
    return {
      qty: seed.qty || 1,
      tier: (typeof seed.tier === 'number') ? seed.tier : -1,
      activeImg: 0,
      sel: seed.sel || [],
      cv: null,
      pdata: { variants: [], media_ids: [] },
      purchaseType: 'one-time',
      sellingPlan: seed.default_selling_plan || null,
      sellingPlans: seed.selling_plans || {},
      sellingPlanRequired: seed.selling_plan_required || {},
      mediaIndex: function (v) {
        if (!v || !v.featured_image) return this.activeImg;
        var img = v.featured_image;
        if (typeof img.position === 'number' && img.position > 0) return img.position - 1;
        var ids = this.pdata.media_ids;
        if (Array.isArray(ids) && img.id != null) {
          var i = ids.findIndex(function (id) { return id === img.id || String(id) === String(img.id); });
          if (i >= 0) return i;
        }
        return this.activeImg;
      },
      syncImg: function (v) {
        var idx = this.mediaIndex(v);
        if (typeof idx === 'number' && idx >= 0) this.activeImg = idx;
      },
      init: function () {
        try { this.pdata = JSON.parse(this.$refs.pdata.textContent) || { variants: [], media_ids: [] }; } catch (_) {}
        var urlId = null;
        try { urlId = new URLSearchParams(window.location.search).get('variant'); } catch (_) {}
        var fromUrl = urlId && this.pdata.variants.find(function (v) { return String(v.id) === urlId; });
        if (fromUrl) {
          this.sel = [fromUrl.option1, fromUrl.option2, fromUrl.option3].filter(function (o) { return o != null; });
        }
        this.cv = variantFrom(this.sel, this.pdata) || this.pdata.variants[0] || null;
        this.syncImg(this.cv);
        this.syncSellingPlan();
      },
      syncVariantInputs: function () {
        // Update form values synchronously before checkout helpers see the change.
        // Alpine also binds these values, but its DOM flush runs asynchronously.
        var scope = this.$root || this.$el;
        if (!scope || !scope.querySelectorAll) return;
        var id = this.cv ? String(this.cv.id) : '';
        scope.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
          input.value = id;
        });
      },
      pick: function (i, val, event) {
        // Variant selection is local UI state, never a form submission or link.
        if (event) { event.preventDefault(); event.stopPropagation(); }
        i = Number(i);
        if (!Number.isInteger(i) || i < 0 || i > 2) return;
        var next = this.sel.slice();
        next[i] = val;
        this.sel = next;
        var v = variantFrom(next, this.pdata);
        // A nonexistent combination must not retain an earlier purchasable ID.
        this.cv = v;
        if (v) this.syncImg(v);
        this.syncSellingPlan();
        this.syncVariantInputs();
        try {
          var url = new URL(window.location.href);
          if (v) url.searchParams.set('variant', v.id);
          else url.searchParams.delete('variant');
          // Preserve filters, tracking parameters, hash and checkout return state.
          window.history.replaceState(window.history.state, '', url.toString());
          rememberStorefrontPath();
        } catch (_) {}
        this.$dispatch && this.$dispatch('variant:change', { variant: v });
      },
      optAvailable: function (i, val) {
        var probe = this.sel.slice();
        probe[i] = val;
        var v = variantFrom(probe, this.pdata);
        return !!(v && v.available);
      },
      price: function () { return this.cv ? this.cv.price : (seed.price || 0); },
      compareAt: function () { return (this.cv && this.cv.compare_at_price) || 0; },
      plansForVariant: function () {
        var id = this.cv && this.cv.id;
        return id == null ? [] : (this.sellingPlans[String(id)] || []);
      },
      syncSellingPlan: function () {
        var plans = this.plansForVariant();
        var current = this.sellingPlan;
        var valid = plans.some(function (p) { return String(p.id) === String(current); });
        this.sellingPlan = valid ? current : (plans[0] ? String(plans[0].id) : null);
        if (!plans.length) this.purchaseType = 'one-time';
        if (this.requiresSellingPlan() && plans.length) this.purchaseType = 'subscribe';
      },
      selectedPlan: function () {
        var current = this.sellingPlan;
        return this.plansForVariant().find(function (p) { return String(p.id) === String(current); }) || null;
      },
      subscriptionPrice: function () {
        var plan = this.selectedPlan();
        return plan ? plan.price : this.price();
      },
      subscriptionCompareAt: function () {
        var plan = this.selectedPlan();
        return plan ? (plan.compare_at_price || this.price()) : this.price();
      },
      requiresSellingPlan: function () {
        var id = this.cv && this.cv.id;
        return !!((id != null && this.sellingPlanRequired[String(id)]) || (this.cv && this.cv.requires_selling_plan));
      },
      choosePurchaseType: function (type) {
        if (type === 'subscribe' && this.plansForVariant().length) {
          this.purchaseType = 'subscribe';
          this.syncSellingPlan();
        } else if (type === 'one-time' && !this.requiresSellingPlan()) {
          this.purchaseType = 'one-time';
        }
      },
      saving: function () { var c = this.compareAt(); return c > this.price() ? Math.round((1 - this.price() / c) * 100) : 0; },
      tierTotal: function (q, amt, dtype) { return money(tierCents(q, this.price(), amt || 0, dtype)); },
      tierUnit: function (q, amt, dtype) { return money(Math.round(tierCents(q, this.price(), amt || 0, dtype) / Math.max(1, q))); },
      available: function () { return !!(this.cv && this.cv.available !== false); },
      canAdd: function () {
        if (!this.available()) return false;
        if (this.purchaseType === 'subscribe') return !!this.sellingPlan;
        return !this.requiresSellingPlan();
      }
    };
  }

  window.clyroShop = {
    add: add,
    whenIdle: whenIdle,
    update: update,
    applyDiscountForCheckout: applyDiscountForCheckout,
    clearIfUnchanged: clearIfUnchanged,
    lockCheckout: function (locked) { checkoutLocked = locked; },
    change: change,
    refresh: refresh,
    openCart: openCart,
    closeCart: closeCart,
    money: money,
    variantFrom: variantFrom,
    quickAddVariant: quickAddVariant,
    buyBox: buyBox
  };

  // Remember a safe storefront return target for Shopify's out-of-stock
  // checkout fallback. Only same-site paths are stored; cart and checkout
  // paths are intentionally excluded to prevent redirect loops.
  function rememberStorefrontPath() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('cart_open');
      var path = url.pathname;
      if (!/(^|\/)cart\/?$/i.test(path)
          && !/(^|\/)checkouts?(?:\/|$)/i.test(path)
          && !/\/pages\/cart-return(?:\/|$)/i.test(path)) {
        window.sessionStorage.setItem('clyro:last-storefront-path', path + url.search + url.hash);
      }
    } catch (_) {}
  }
  rememberStorefrontPath();
  // Filtering can change the URL through history.pushState without a reload.
  window.addEventListener('pagehide', rememberStorefrontPath);

  var checkoutNavigationStarted = false;

  function checkoutControlFromEvent(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches
          && node.matches('a,button,input[type="submit"],[role="button"]')) return node;
    }
    var target = event.target;
    return target && target.closest
      ? target.closest('a,button,input[type="submit"],[role="button"]')
      : null;
  }

  function onCheckoutIntent(event) {
    if (checkoutNavigationStarted || event.isTrusted === false) return;
    var control = checkoutControlFromEvent(event);
    if (!control || !control.hasAttribute('data-clyro-checkout-fallback')) return;
    if (control.matches && control.matches('[data-cd-checkout]')) return;

    var label = String(
      control.getAttribute('aria-label')
      || control.value
      || control.textContent
      || ''
    ).replace(/\s+/g, ' ').trim().toLowerCase();
    var href = control.getAttribute('href') || control.getAttribute('formaction') || '';
    var explicitCheckout = /^(checkout|checkout now|proceed to checkout|go to checkout)(\b|\s|$)/i.test(label);
    var wrongCartTarget = false;
    var placeholderTarget = !href || href === '#' || /^javascript:/i.test(href);

    try {
      if (href) {
        var parsed = new URL(href, window.location.href);
        wrongCartTarget = /\/cart\/?$/i.test(parsed.pathname);
        // A correct checkout URL is left entirely to the owning cart/Shopify.
        if (/\/checkouts?\//i.test(parsed.pathname) || /\/checkout\/?$/i.test(parsed.pathname)) return;
      }
    } catch (_) {}

    // Safety fallback for any retired/third-party Checkout Now control that
    // still points to /cart or has no destination.
    if (!explicitCheckout || (!wrongCartTarget && !placeholderTarget)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutNavigationStarted = true;
    try {
      document.dispatchEvent(new CustomEvent('clyro:checkout-started', {
        detail: { source: 'checkout-guard' }
      }));
    } catch (_) {}
    control.setAttribute('aria-busy', 'true');
    if ('disabled' in control) control.disabled = true;
    window.location.assign(cartUrl('checkout'));
  }

  // Seed the cart store from Shopify's server-rendered count. Do not request
  // /cart.js on page load: cart apps can observe that request and mistake it
  // for a customer cart action.
  document.addEventListener('alpine:init', function () {
    var initialCount = 0;

    try {
      var badge = document.querySelector('[data-clyro-cart-count]');

      if (badge) {
        initialCount = Number(
          badge.getAttribute('data-cart-initial-count') || badge.textContent || 0
        ) || 0;
      }
    } catch (_) {}

    window.Alpine.store('cart', Object.assign({}, BASE_CART, {
      item_count: Math.max(0, initialCount)
    }));

    // Important: never call refresh() here. Page-load cart requests add network
    // work and can cause third-party compatibility scripts to open UI.
  });

  document.addEventListener('click', onCartTriggerClick, true);
  document.addEventListener('click', onCheckoutIntent, true);
  window.clyroCartIntentReady = true;
  var earlyCartIntent = window.clyroEarlyCartIntent;
  window.clyroEarlyCartIntent = null;
  if (earlyCartIntent && Date.now() - earlyCartIntent.at < 10000) {
    onCartTriggerClick(earlyCartIntent.event);
    var earlyTrigger = earlyCartIntent.event.target.closest('[data-cart-drawer-open], #clyro-cart-toggle');
    if (earlyTrigger) earlyTrigger.removeAttribute('aria-busy');
  }
})();
